import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const AUTH_URL = "/auth";
const EMPLOYEES_URL = "/employees";

export const api = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE,
    prepareHeaders: (headers, { getState }) => {
      headers.set("accept", `*/*`);
      // Check if the endpoint requires a specific Content-Type
      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", `application/json`);
      }
      const { accessToken } = getState().auth;
      if (accessToken) {
        headers.set("authorization", `Bearer ${accessToken}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => {
        const authHeader =
          "Basic " + btoa(credentials.email + ":" + credentials.password);
        return {
          url: `${AUTH_URL}/p/token-management/login`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: authHeader,
          },
        };
      },
    }),

    signup: builder.mutation({
      query: (credentials) => {
        return {
          url: `${AUTH_URL}/p/user-account-management/users`,
          method: "POST",
          body: credentials,
        };
      },
    }),

    refreshToken: builder.mutation({
      query: (refreshToken) => {
        return {
          url: `${AUTH_URL}/p/token-management/refresh`,
          method: "POST",
          params: refreshToken,
        };
      },
    }),

    getSSOLoginUrl: builder.mutation({
      query: (provider) => ({
        url: `${AUTH_URL}/p/token-management/openid/login?provider=${provider}`,
      }),
    }),

    getSSOSignupUrl: builder.mutation({
      query: (provider) => ({
        url: `${AUTH_URL}/p/user-account-management/users/openid?provider=${provider}`,
        method: "POST",
      }),
    }),

    getLinkSSOProviderUrl: builder.mutation({
      query: (provider) => ({
        url: `${AUTH_URL}/identity-provider-management/identities?provider=${provider}`,
        method: "POST",
      }),
    }),

    callbackSSOLogin: builder.query({
      query: ({ code, state }) => ({
        url: `${AUTH_URL}/p/token-management/openid/callback?code=${code}&state=${state}`,
      }),
    }),

    callbackSSOSignup: builder.query({
      query: ({ code, state }) => ({
        url: `${AUTH_URL}/p/user-account-management/users/openid/callback?code=${code}&state=${state}`,
      }),
    }),

    callbackSSOLinkIdentity: builder.query({
      query: ({ code, state }) => ({
        url: `${AUTH_URL}/identity-provider-management/callback?code=${code}&state=${state}`,
      }),
    }),

    logoutSSOProvider: builder.mutation({
      query: () => {
        return {
          url: `${AUTH_URL}/user-account-management/users/logout`,
          method: "post",
        };
      },
    }),

    unlinkSSOProvider: builder.mutation({
      query: (id) => {
        return {
          url: `${AUTH_URL}/identity-provider-management/identities/${id}`,
          method: "DELETE",
        };
      },
    }),

    getUser: builder.mutation({
      query: () => ({
        url: `${AUTH_URL}/user-account-management/users/me`,
      }),
    }),

    identitiesListing: builder.query({
      query: () => ({
        url: `${AUTH_URL}/identity-management/identities`,
      }),
    }),

    addIdentity: builder.mutation({
      query: (data) => ({
        url: `${AUTH_URL}/identity-management/identities`,
        method: "POST",
        body: data,
      }),
    }),

    deleteIdentity: builder.mutation({
      query: ({ identityId }) => ({
        url: `${AUTH_URL}/identity-management/identities/${identityId}`,
        method: "DELETE",
      }),
    }),

    resetPassword: builder.mutation({
      query: (data) => ({
        url: `${AUTH_URL}/user-account-management/users/password`,
        method: "PUT",
        body: data,
      }),
    }),

    updateProfile: builder.mutation({
      query: (data) => ({
        url: `${AUTH_URL}/user-account-management/users`,
        method: "PUT",
        body: data,
      }),
    }),

    tokenVerification: builder.mutation({
      query: (token) => {
        return {
          url: `${AUTH_URL}/p/token-management/verify-user`,
          method: "PUT",
          body: token,
          headers: {
            "Content-Type": "text/plain",
          },
        };
      },
    }),

    identityTokenVerification: builder.mutation({
      query: (token) => {
        return {
          url: `${AUTH_URL}/p/token-management/verify-identity`,
          method: "PUT",
          body: token,
          headers: {
            "Content-Type": "text/plain",
          },
        };
      },
    }),

    profileTokenVerification: builder.mutation({
      query: (token) => {
        return {
          url: `${AUTH_URL}/p/token-management/verify-profile-email`,
          method: "PUT",
          body: token,
          headers: {
            "Content-Type": "text/plain",
          },
        };
      },
    }),

    forgotPassword: builder.mutation({
      query: (data) => {
        return {
          url: `${AUTH_URL}/p/user-account-management/users/request-reset-password`,
          method: "POST",
          body: data,
        };
      },
    }),
    resetPasswordByToken: builder.mutation({
      query: (data) => {
        return {
          url: `${AUTH_URL}/p/token-management/password`,
          method: "PUT",
          body: data,
        };
      },
    }),

    getEmployeeListing: builder.mutation({
      query: (data) => {
        return {
          url: `${EMPLOYEES_URL}/employee-management/employee-accounts`,
          method: "GET",
          params: data,
        };
      },
    }),

    updateEmployee: builder.mutation({
      query: (data) => {
        return {
          url: `${EMPLOYEES_URL}/employee-management/employee-accounts/${data.id}`,
          method: "PUT",
          body: data,
        };
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useResetPasswordMutation,
  useSignupMutation,
  useUpdateProfileMutation,
  useRefreshTokenMutation,
  useGetUserMutation,
  useIdentitiesListingQuery,
  useAddIdentityMutation,
  useDeleteIdentityMutation,
  useTokenVerificationMutation,
  useForgotPasswordMutation,
  useResetPasswordByTokenMutation,
  useGetEmployeeListingMutation,
  useUpdateEmployeeMutation,
  useProfileTokenVerificationMutation,
  useGetSSOLoginUrlMutation,
  useCallbackSSOLoginQuery,
  useGetSSOSignupUrlMutation,
  useCallbackSSOSignupQuery,
  useCallbackSSOLinkIdentityQuery,
  useGetLinkSSOProviderUrlMutation,
  useLogoutSSOProviderMutation,
  useUnlinkSSOProviderMutation,
  useIdentityTokenVerificationMutation,
} = api;
